import React from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import type { PdfExtractionReport } from "../services/extractorApi";

interface PdfExtractionReportModalProps {
  open: boolean;
  onClose: () => void;
  report: PdfExtractionReport | null;
  filename?: string;
}

const KpiCard: React.FC<{
  label: string;
  value: string | number;
  color?: string;
  subtitle?: string;
}> = ({ label, value, color = "#1F3864", subtitle }) => (
  <Box
    sx={{
      p: 2,
      borderRadius: 2,
      bgcolor: "#F7F9FC",
      border: "1px solid",
      borderColor: "divider",
      height: "100%",
    }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
      {label}
    </Typography>
    <Typography variant="h4" sx={{ fontWeight: 700, color, mt: 0.5, lineHeight: 1.1 }}>
      {value}
    </Typography>
    {subtitle && (
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
        {subtitle}
      </Typography>
    )}
  </Box>
);

export const PdfExtractionReportModal: React.FC<PdfExtractionReportModalProps> = ({
  open,
  onClose,
  report,
  filename,
}) => {
  if (!report) return null;

  const hasFailures = report.windows_failed > 0;
  const hasMissing = report.audit_missing.length > 0;
  const reviewAlerts = [...(report.review_alerts ?? [])].sort((a, b) => a.pagina - b.pagina);
  const hasWarnings =
    hasFailures || hasMissing || report.breve_still_over_limit > 0 || reviewAlerts.length > 0;

  const recallPct =
    report.audit_expected_count && report.audit_expected_count > 0
      ? Math.round(((report.audit_found_count ?? 0) / report.audit_expected_count) * 100)
      : null;

  const statusIcon = hasFailures
    ? <ErrorOutlineIcon sx={{ color: "#C62828" }} />
    : hasWarnings
      ? <WarningAmberIcon sx={{ color: "#ED6C02" }} />
      : <CheckCircleOutlineIcon sx={{ color: "#2E7D32" }} />;

  const elapsedLabel = report.elapsed_seconds >= 60
    ? `${Math.floor(report.elapsed_seconds / 60)} min ${Math.round(report.elapsed_seconds % 60)} s`
    : `${Math.round(report.elapsed_seconds)} s`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {statusIcon}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Conversione completata
            </Typography>
            {filename && (
              <Typography variant="caption" color="text.secondary">
                {filename}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Il file Excel nel formato IMPORT (Capitolo · Codice · Descrizione breve/estesa · UdM ·
          Quantità) è stato scaricato. L'estrazione è stata eseguita dal modello partner (MP)
          pagina per pagina, con audit di recall sui progressivi.
        </Typography>

        <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
          <Grid size={{ xs: 6, sm: 4 }}>
            <KpiCard label="Voci estratte" value={report.total_items} />
          </Grid>
          <Grid size={{ xs: 6, sm: 4 }}>
            <KpiCard label="Capitoli" value={report.total_chapters} />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <KpiCard
              label="Tempo di elaborazione"
              value={elapsedLabel}
              subtitle={`${report.total_pages} pagine · ${report.total_windows} finestre`}
            />
          </Grid>
        </Grid>

        <Box sx={{ mb: 2.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Recall (audit sui progressivi)
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
            {recallPct != null ? (
              <Chip
                size="small"
                icon={recallPct >= 100 ? <CheckCircleOutlineIcon /> : <WarningAmberIcon />}
                label={`${report.audit_found_count}/${report.audit_expected_count} trovati (${recallPct}%)`}
                sx={
                  recallPct >= 100
                    ? { bgcolor: "#E8F5E9", color: "#2E7D32" }
                    : { bgcolor: "#FFF8E1", color: "#F57C00" }
                }
              />
            ) : (
              <Typography variant="caption" color="text.secondary">
                Nessun progressivo numerico rilevato per l'audit.
              </Typography>
            )}
            {report.audit_passes > 0 && (
              <Chip size="small" label={`${report.audit_passes} passate di ri-estrazione`} />
            )}
          </Box>
          {hasMissing && (
            <Typography variant="caption" sx={{ color: "#C62828", display: "block", mt: 1 }}>
              Progressivi mancanti: {report.audit_missing.join(", ")}
            </Typography>
          )}
          {report.audit_duplicated.length > 0 && (
            <Typography variant="caption" sx={{ color: "#ED6C02", display: "block", mt: 0.5 }}>
              Progressivi duplicati: {report.audit_duplicated.join(", ")}
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: hasFailures ? 2 : 0 }}>
          <Chip
            size="small"
            icon={<CheckCircleOutlineIcon />}
            label={`Finestre riuscite: ${report.windows_ok}`}
            sx={{ bgcolor: "#E8F5E9", color: "#2E7D32" }}
          />
          {hasFailures && (
            <Chip
              size="small"
              icon={<ErrorOutlineIcon />}
              label={`Finestre fallite: ${report.windows_failed}`}
              sx={{ bgcolor: "#FFEBEE", color: "#C62828" }}
            />
          )}
          {report.breve_still_over_limit > 0 && (
            <Chip
              size="small"
              icon={<WarningAmberIcon />}
              label={`${report.breve_still_over_limit} descrizioni brevi oltre 100 caratteri`}
              sx={{ bgcolor: "#FFF8E1", color: "#F57C00" }}
            />
          )}
        </Box>

        {hasFailures && (
          <Box sx={{ p: 1.5, bgcolor: "#FFEBEE", borderRadius: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
              Le pagine sotto non sono state estratte automaticamente — verificale manualmente sul PDF originale.
            </Typography>
            <List dense disablePadding>
              {report.failures.map((f) => (
                <ListItem key={f.window_index} disablePadding sx={{ py: 0.25 }}>
                  <ListItemText
                    primary={`Pagine ${f.pages.join(", ")}`}
                    secondary={`${f.error} (${f.attempts} tentativi)`}
                    primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
                    secondaryTypographyProps={{ variant: "caption" }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {reviewAlerts.length > 0 && (
          <Box sx={{ mt: 2, p: 1.5, bgcolor: "#FFF8E1", borderRadius: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: "#F57C00" }}>
              Righe da verificare ({reviewAlerts.length})
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
              Su queste righe è stata applicata una correzione automatica: controllale sul PDF originale
              alla pagina indicata.
            </Typography>
            <List dense disablePadding sx={{ maxHeight: 280, overflowY: "auto" }}>
              {reviewAlerts.map((a, i) => (
                <ListItem key={i} disablePadding sx={{ py: 0.5, alignItems: "flex-start" }}>
                  <ListItemText
                    primary={`Pag. ${a.pagina}${a.codice ? ` · ${a.codice}` : ""}${a.descrizione ? ` · ${a.descrizione}` : ""}`}
                    secondary={a.motivo}
                    primaryTypographyProps={{ variant: "body2", fontWeight: 600, sx: { wordBreak: "break-word" } }}
                    secondaryTypographyProps={{ variant: "caption" }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained" color="error">
          Ho capito
        </Button>
      </DialogActions>
    </Dialog>
  );
};
